// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, StyleSheet, TextInput, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';

import { Text } from '@/components/Themed';
import GoffyButton from '@/components/GoffyButton';
import { useUser } from '@/app/user-context';
import { useI18n } from '@/i18n';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { getSharedBffWebSocket } from '@/services/bffWebSocket';
import { createRequestId } from '@/services/requestId';
import appColors from '@/theme/colors';

const ACK_TIMEOUT_MS = 12000;

/** Points-only round logging: one number, no per-hole detail. */
export default function LogTotalScoreScreen() {
  const { roundId } = useLocalSearchParams<{ roundId?: string }>();
  const { user } = useUser();
  const { t } = useI18n();
  const router = useRouter();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);

  const [playerId, setPlayerId] = useState<string | null>(null);
  const [grossStrokes, setGrossStrokes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.token || !user?.id) return;
    const controller = new AbortController();
    fetch(`${apiBaseUrl}/api/player/by-user/${user.id}`, {
      headers: { Authorization: `Bearer ${user.token}` },
      signal: controller.signal,
    })
      .then(async (response) => (response.ok ? ((await response.json()) as { id?: string }) : null))
      .then((payload) => {
        if (payload?.id) setPlayerId(payload.id);
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, [apiBaseUrl, user?.id, user?.token]);

  const strokes = Number.parseInt(grossStrokes, 10);
  const canSubmit = Boolean(roundId && playerId && Number.isFinite(strokes) && strokes > 0 && !saving);

  const handleSave = () => {
    if (!roundId || !playerId || !Number.isFinite(strokes) || strokes <= 0) return;
    setSaving(true);
    setError(null);

    const socket = getSharedBffWebSocket(apiBaseUrl);
    socket.setAuthToken(user?.token ?? null);
    const requestId = createRequestId();
    let settled = false;

    const timeoutHandle = setTimeout(() => {
      if (settled) return;
      settled = true;
      unsubscribe();
      setSaving(false);
      setError(t('logTotalScore.saveFailed'));
    }, ACK_TIMEOUT_MS);

    const unsubscribe = socket.onMessage((message) => {
      const payload = message as Record<string, unknown>;
      const type = typeof payload.type === 'string' ? payload.type : null;
      if (payload.requestId !== requestId) return;
      if (type === 'round.manualScore.logged') {
        settled = true;
        clearTimeout(timeoutHandle);
        unsubscribe();
        router.replace('/my-rounds');
        return;
      }
      if (type === 'round.manualScore.error') {
        settled = true;
        clearTimeout(timeoutHandle);
        unsubscribe();
        setSaving(false);
        setError(t('logTotalScore.saveFailed'));
      }
    });

    const sent = socket.send({
      type: 'round.manualScore.log',
      requestId,
      roundId,
      playerId,
      grossStrokes: strokes,
    });
    if (sent === 'dropped') {
      settled = true;
      clearTimeout(timeoutHandle);
      unsubscribe();
      setSaving(false);
      setError(t('logTotalScore.saveFailed'));
    }
  };

  return (
    <View style={styles.screen}>
      <Text style={styles.label}>{t('logTotalScore.grossStrokesLabel')}</Text>
      <TextInput
        style={styles.input}
        value={grossStrokes}
        onChangeText={setGrossStrokes}
        keyboardType="numeric"
        placeholder="0"
        placeholderTextColor={appColors.text.muted}
        testID="log-total-score-input"
        autoFocus
      />
      {error ? <Text style={styles.error}>{error}</Text> : null}
      <GoffyButton
        label={saving ? t('logTotalScore.saving') : t('logTotalScore.saveButton')}
        onPress={handleSave}
        disabled={!canSubmit}
        testID="log-total-score-save"
      />
      {saving ? <ActivityIndicator size="small" color={appColors.accent.primary} style={styles.spinner} /> : null}
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: appColors.background.app,
    padding: 16,
    gap: 10,
  },
  label: {
    fontSize: 13,
    fontWeight: '700',
    color: appColors.text.muted,
  },
  input: {
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panelAlt,
    borderRadius: 10,
    paddingHorizontal: 13,
    paddingVertical: 12,
    fontSize: 20,
    fontWeight: '700',
    color: appColors.text.heading,
    maxWidth: 140,
  },
  error: {
    fontSize: 13,
    color: appColors.status.dangerText,
  },
  spinner: {
    marginTop: 4,
  },
});
